import { useState, useRef } from 'react';
import { Moon, Sun, Shield, Search, X, Download, Upload, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ClubViajemosLogoFull } from './ClubViajemosLogo';
import LanguageSwitcher from './LanguageSwitcher';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

// Simple XOR-based encryption for credential backup
const encryptData = (data, password) => {
  const encoded = btoa(unescape(encodeURIComponent(data)));
  let result = '';
  for (let i = 0; i < encoded.length; i++) {
    result += String.fromCharCode(encoded.charCodeAt(i) ^ password.charCodeAt(i % password.length));
  }
  return btoa(result);
};

const decryptData = (encrypted, password) => {
  const decoded = atob(encrypted);
  let result = '';
  for (let i = 0; i < decoded.length; i++) {
    result += String.fromCharCode(decoded.charCodeAt(i) ^ password.charCodeAt(i % password.length));
  }
  return decodeURIComponent(escape(atob(result)));
};

export const Header = ({ isDark, toggleTheme, searchQuery, onSearchChange, onExport, onImport, presentationMode, onTogglePresentationMode }) => {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const fileInputRef = useRef(null);

  const handleExport = () => {
    if (!exportPassword || exportPassword.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }
    const rawData = onExport();
    const parsed = JSON.parse(rawData);
    if (Object.keys(parsed).length === 0) {
      toast.error('No hay credenciales para exportar');
      return;
    }
    const encrypted = encryptData(rawData, exportPassword);
    const blob = new Blob([JSON.stringify({ v: 1, data: encrypted })], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fullpagescv_backup_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExportDialog(false);
    setExportPassword('');
    toast.success('Backup exportado correctamente');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const content = JSON.parse(evt.target.result);
        if (!content.data || content.v !== 1) {
          toast.error('Archivo de backup inválido');
          return;
        }
        const decrypted = decryptData(content.data, importPassword);
        const count = onImport(decrypted, false);
        setShowImportDialog(false);
        setImportPassword('');
        toast.success(`${count} plataformas importadas correctamente`);
      } catch {
        toast.error('Contraseña incorrecta o archivo corrupto');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 w-full bg-white/95 dark:bg-[#0B0F19]/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 sm:h-24 gap-3 min-w-0">
          <ClubViajemosLogoFull />

          {/* Right Side */}
          <div className="flex items-center gap-1.5 flex-1 sm:flex-initial min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              onClick={() => { window.location.href = '/educacion'; }}
              data-testid="header-soy-travel-empire-link"
              className="cv-gold-btn shrink-0 inline-flex items-center gap-1.5 text-xs sm:text-sm font-extrabold px-3 sm:px-4 py-1.5 rounded-full uppercase tracking-wide whitespace-nowrap"
            >
              Educacion Online
            </button>
            <a
              href="/plandeagencias"
              data-testid="header-precios-link"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-[#1D1D1B] dark:text-white hover:text-white px-3 sm:px-4 py-1.5 rounded-full border border-[#45A0EE]/40 hover:bg-[#45A0EE] hover:border-[#45A0EE] transition-colors uppercase tracking-wide whitespace-nowrap"
            >
              Precios
            </a>
            <a
              href="/peru"
              data-testid="header-peru-link"
              className="hidden shrink-0 inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-[#1D1D1B] dark:text-white hover:text-white px-3 sm:px-4 py-1.5 rounded-full border border-[#80C753]/40 hover:bg-[#80C753] hover:border-[#80C753] transition-colors uppercase tracking-wide whitespace-nowrap"
            >
              Viajes a Perú
            </a>
            <LanguageSwitcher />

            {/* Theme Toggle */}
            <Button
              data-testid="theme-toggle"
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="h-9 w-9 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {isDark ? (
                <Sun className="h-5 w-5 text-[#45A0EE]" />
              ) : (
                <Moon className="h-5 w-5 text-[#1D1D1B]" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-[#80C753]" />
              Exportar Credenciales
            </DialogTitle>
            <DialogDescription>
              Crea un backup encriptado de todas tus credenciales.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Contraseña de encriptación
              </label>
              <Input
                data-testid="export-password-input"
                type="password"
                placeholder="Mínimo 4 caracteres"
                value={exportPassword}
                onChange={(e) => setExportPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleExport()}
              />
              <p className="text-xs text-slate-500">
                Necesitarás esta contraseña para importar el backup.
              </p>
            </div>
            <Button
              data-testid="confirm-export-btn"
              onClick={handleExport}
              className="w-full bg-[#80C753] hover:bg-[#6ab043] text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar Backup
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#45A0EE]" />
              Importar Credenciales
            </DialogTitle>
            <DialogDescription>
              Restaura tus credenciales desde un backup encriptado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Contraseña del backup
              </label>
              <Input
                data-testid="import-password-input"
                type="password"
                placeholder="Contraseña usada al exportar"
                value={importPassword}
                onChange={(e) => setImportPassword(e.target.value)}
              />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              data-testid="confirm-import-btn"
              onClick={() => {
                if (!importPassword || importPassword.length < 4) {
                  toast.error('Ingresa la contraseña del backup');
                  return;
                }
                fileInputRef.current?.click();
              }}
              className="w-full bg-[#45A0EE] hover:bg-[#3590de] text-white"
            >
              <Upload className="h-4 w-4 mr-2" />
              Seleccionar Archivo de Backup
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Las credenciales importadas se combinarán con las existentes.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
